import React, { useState, useEffect, useCallback } from 'react';
import { Track } from 'livekit-client';
import { useRemoteParticipants } from '@livekit/components-react';
import { Mic, MicOff, MessageSquare, X } from 'lucide-react';

import { useAgentControlBar } from '../../hooks/use-agent-control-bar';
import type { AppConfig } from '../../types/types';
import { cn } from '../../utils/utils';
import { TrackToggle } from './ui';

interface AgentControlBarProps extends React.HTMLAttributes<HTMLDivElement> {
    capabilities: Pick<AppConfig, 'supportsChatInput' | 'supportsVideoInput' | 'supportsScreenShare'>;
    onChatOpenChange?: (open: boolean) => void;
    onSendMessage?: (message: string) => Promise<void>;
    onDisconnect?: () => void;
    onDeviceError?: (error: { source: Track.Source; error: Error }) => void;
    className?: string;
    controls?: any;
    hasActiveVideo?: boolean;
    saveUserChoices?: boolean;
    isWidget?: boolean;
}

/**
 * Control bar for voice assistant interactions.
 * Provides controls for microphone, chat/transcribe, and end call.
 */
export const AgentControlBar = ({
    capabilities,
    onChatOpenChange,
    onSendMessage,
    onDisconnect,
    onDeviceError,
    className,
    controls,
    saveUserChoices = true,
    isWidget = false,
    hasActiveVideo = false,
    ...props
}: AgentControlBarProps) => {
    const participants = useRemoteParticipants();
    const [chatOpen, setChatOpen] = useState(false);
    const [isSendingMessage, setIsSendingMessage] = useState(false);
    const [isDisconnecting, setIsDisconnecting] = useState(false);

    const isAgentConnected = participants.some((p) => p.isAgent);

    // Get theme color from context or use default blue
    // For now we'll stick to classes, assuming the parent passes theme-aware classes if needed
    // or we use standard colors that look good with the gradient.

    const {
        visibleControls,
        microphoneToggle,
        handleDisconnect,
    } = useAgentControlBar({ controls, saveUserChoices });

    const handleSendMessage = async (message: string) => {
        if (!message.trim()) return;

        setIsSendingMessage(true);
        try {
            await onSendMessage?.(message);
        } catch (error) {
            console.error('❌ Error sending message:', error);
        } finally {
            setIsSendingMessage(false);
        }
    };

    const onLeave = async () => {
        setIsDisconnecting(true);
        try {
            await handleDisconnect();
            onDisconnect?.();
        } catch (error) {
            console.error('❌ Error during disconnect:', error);
        } finally {
            setIsDisconnecting(false);
        }
    };

    useEffect(() => {
        onChatOpenChange?.(chatOpen);
    }, [chatOpen, onChatOpenChange]);

    const onMicrophoneDeviceSelectError = useCallback(
        (error: Error) => onDeviceError?.({ source: Track.Source.Microphone, error }),
        [onDeviceError]
    );

    // Styles matching the reference:
    // Chat: Left, small, glass/white
    // Mic: Center, large, blue/primary solid
    // End: Right, small, glass/white (X icon)

    const glassButtonStyles = "h-14 w-14 rounded-full flex items-center justify-center backdrop-blur-md bg-white/20 dark:bg-white/10 hover:bg-white/30 transition-all duration-300 shadow-sm border border-white/20";
    // Modified primary to match size (h-14) but keep emphasis styles
    const primaryButtonStyles = "h-14 w-14 rounded-full flex items-center justify-center bg-blue-600 hover:bg-blue-500 text-white shadow-lg shadow-blue-500/30 transition-all duration-300 transform hover:scale-105 active:scale-95";

    return (
        <div
            aria-label="Voice assistant controls"
            className={cn(
                'flex items-center justify-center gap-6 mb-4 pb-2', // Reduced bottom margin/padding to move down (relative to center) or closer to edge? "move slightly down" usually means closer to bottom. The previous was mb-8, mb-4 pushes it lower in a flex-col layout if it's effectively "padding bottom". Wait. if it's a flex-end container, less margin means LOWER. If it's a center container with margin, it's just spacing.
                // Assuming it's at the bottom of a flex-col container, `mb-8` pushes it UP. `mb-4` allows it to be lower.
                className
            )}
            {...props}
        >
            {/* CHAT/TRANSCRIBE BUTTON (LEFT) */}
            {visibleControls.chat && (
                <button
                    onClick={() => setChatOpen(!chatOpen)}
                    disabled={!isAgentConnected}
                    className={cn(
                        glassButtonStyles,
                        chatOpen ? "bg-white/40 ring-2 ring-white/50" : ""
                    )}
                    aria-label="Toggle transcribe"
                >
                    <MessageSquare
                        className="w-6 h-6 text-gray-800 dark:text-white/90"
                        strokeWidth={2}
                    />
                </button>
            )}

            {/* MICROPHONE BUTTON (CENTER - MAIN) */}
            {visibleControls.microphone && (
                <button
                    onClick={() => microphoneToggle.toggle(!microphoneToggle.enabled)}
                    disabled={microphoneToggle.pending}
                    className={cn(
                        primaryButtonStyles,
                        microphoneToggle.pending && "opacity-80 cursor-wait"
                    )}
                    aria-label="Toggle microphone"
                >
                    {microphoneToggle.enabled ? (
                        <Mic className="w-6 h-6" strokeWidth={2} />
                    ) : (
                        <MicOff className="w-6 h-6" strokeWidth={2} />
                    )}
                </button>
            )}

            {/* END CALL BUTTON (RIGHT) */}
            {visibleControls.leave && (
                <button
                    onClick={onLeave}
                    disabled={isDisconnecting}
                    className={cn(
                        glassButtonStyles,
                        "hover:bg-red-500/20 hover:border-red-500/30" // Subtle red hover for danger action
                    )}
                    aria-label="End call"
                >
                    <X
                        className="w-6 h-6 text-gray-800 dark:text-white/90"
                        strokeWidth={2}
                    />
                </button>
            )}
        </div>
    );
};